import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { usePreviousValue } from 'beautiful-react-hooks';

import "./MapBox.css";
import Layers from './Layers';

const clickListener = (map, setSelection) => {
  map.on('click', function (e) {
    // set bbox as 5px reactangle area around clicked point
    var bbox = [
      [e.point.x - 5, e.point.y - 5],
      [e.point.x + 5, e.point.y + 5]
    ];
    var features = map.queryRenderedFeatures(bbox, {
      layers: ['3d-building-layer', 'line-layer']
    });

    const clicked = features.map(f => f.properties.id)
    // don't allow multi-select (confusing)
    if (clicked.length > 0) {
      setSelection([clicked[0]]);
    }
  });

  map.on('mouseenter', '3d-building-layer', function () {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', '3d-building-layer', function () {
    map.getCanvas().style.cursor = '';
  });

  map.on('mouseenter', 'line-layer', function () {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', 'line-layer', function () {
    map.getCanvas().style.cursor = '';
  });
}


const moveListener = (map, setBounds) => {
  map.on('moveend', e => {
    if (map.getLayer('3d-building-layer') && map.getLayer('line-layer')) {
      var bounds = map.getBounds();
      setBounds(bounds);
    }
  })
}


const MapboxGLMap = (props) => {
  const { center, zoom, layerList, opacityMap, data, selection, setSelection, setBounds, geocodeRef } = props;
  const mapContainer = useRef();
  const mapRef = useRef();
  const [loaded, setLoaded] = useState(false);
  const layers = useMemo(() => Layers({ data }), [data]);
  const prevOpacity = usePreviousValue(opacityMap);

  useEffect(() => {
    // process.env.REACT_APP_MAPBOX_KEY;
    mapboxgl.accessToken = 'pk.eyJ1IjoiZ2lyYWZmZXRlY2hub2xvZ3kiLCJhIjoiY2pvMG1ndjBoMDJkejNwbngya2VyenpqMyJ9.RyxX4VKzdQd-GfcsVhyKwg'
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/giraffetechnology/ckbvl6yqh03ip1iqi5meojs7c',
      center: center || [151.2099, -33.8651],
      zoom: zoom || 14,
    });
    mapRef.current = map
    window.map = map

    map.on("style.load", () => {
      map.resize();
      // add in reverse order
      for (let ix = layerList.length - 1; ix >= 0; ix--) {
        map.addLayer(layerList[ix])
      }
      setLoaded(true);
    });
    clickListener(map, setSelection)
    moveListener(map, setBounds)
    // GeoCode
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      marker: false
    });
    geocodeRef.current.appendChild(geocoder.onAdd(map));
    // OR Over Map
    // map.addControl(
    //   new MapboxGeocoder({
    //     accessToken: mapboxgl.accessToken,
    //     mapboxgl: mapboxgl,
    //     marker: false
    //   })
    // );
    // Map Controls
    map.addControl(new mapboxgl.NavigationControl());
    return () => {
      map.remove()
    }
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const { current: map } = mapRef;
    let prevLayerId = layerList[0].id;
    for (let ix = 1; ix < layerList.length; ix++) {
      const thisLayerId = layerList[ix].id;
      map.moveLayer(thisLayerId, prevLayerId);
      prevLayerId = thisLayerId;
    }
  }, [layerList])

  useEffect(() => {
    // TODO turn off if opacity is 0
    const { current: map } = mapRef;
    Object.keys(opacityMap).forEach(lId => {
      if (prevOpacity === undefined) return;
      // if (opacityMap[lId] !== prevOpacity[lId]) {
      map.setLayoutProperty(
        lId,
        `visibility`,
        opacityMap[lId] > 0 ? 'visible' : 'none'
      );
      map.setPaintProperty(
        lId,
        `${map.getLayer(lId).type}-opacity`,
        opacityMap[lId]
      );
      // }
    })
  }, [opacityMap, loaded])

  useEffect(() => {
    const { current: map } = mapRef;
    // const filter = ['in', 'id', ['literal', selection]]
    const filter = ["any", ...selection.map(id => ["==", "id", id])];

    const notFilter = ["all", ...selection.map(id => ["!=", "id", id])];

    if (map.getLayer('3d-building-layer')) {
      map.setFilter('3d-building-layer', notFilter);
      map.setFilter('line-layer', notFilter);
    }
    if (map.getLayer('3d-building-layer-selected')) {
      map.setFilter('3d-building-layer-selected', filter);
      map.setFilter('line-layer-selected', filter);
    }
  }, [selection, data, loaded])

  // const layerKey = hashCode(layers);

  useEffect(() => {
    console.log('mapops')
    const { current: map } = mapRef;
    // if (!(map && map.isStyleLoaded())) return
    if (!map) return

    try {
      layers.forEach(layer => {
        if (map.getLayer(layer.id)) {
          map.removeLayer(layer.id)
        }
        if (map.getSource(layer.id)) {
          map.removeSource(layer.id)
        }
        map.addLayer(layer)
      })
    } catch {
      console.log('map not loaded')
    }

    // TODO update map.getSource('points').setData(geoJSON)
  }, [layers, loaded])

  // Update bounds to match incoming bounds
  // useEffect(() => {
  //   if (!bounds) return
  //   const { current: map } = mapRef
  //   if (!map) return
  //   map.fitBounds(bounds, { padding: 100, duration: 2000 })
  // }, [bounds])

  useEffect(() => {
    if (!center) return
    const { current: map } = mapRef
    if (!map) return
    map.setCenter(center)
  }, [center])

  return <div ref={mapContainer} className={"map-container"} />;
};

export default MapboxGLMap;

